import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiDialogModule, OuiDialogService } from 'omnium-ui/dialog';
import { OuiTableModule } from 'omnium-ui/table';
import { OuiTagModule } from 'omnium-ui/tag';
import { appRoutePaths } from '../../../../app.paths';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../core/auth/action-authorization.service';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import { InvestorCardComponent } from '../../../../core/components/investor-card/investor-card.component';
import {
  ArianeDuplicateDifference,
  ArianeDuplicateDeletionSuggestion,
  ArianeDuplicateInspection,
  InvestorInspection,
} from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { getMetadataBadgeLabel, getMetadataBadgeStatus } from '../../shared/metadata-inspection.utils';
import { countDistinctMetadataIds, formatAtlasGerePar, formatAtlasPersonnePhysiqueIds } from '../../shared/atlas-inspection.utils';
import { InvestorDetailsComponent } from '../../shared/investor-details/investor-details.component';
import { InvestorDeletionScoreDetailsComponent } from '../../shared/investor-deletion-score-details/investor-deletion-score-details.component';
import { InvestorStatusTagComponent } from '../../shared/investor-status-tag/investor-status-tag.component';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { SummaryGridComponent, SummaryGridItem } from '../../shared/summary-grid/summary-grid.component';
import { InvestorPurgeCheckDialogComponent } from '../investor-inspection/components/investor-purge-check-dialog/investor-purge-check-dialog.component';

type ArianeDeletionCandidateView = ArianeDuplicateDeletionSuggestion & {
  investor?: InvestorInspection;
};

type InvestorComparisonRowKey =
  | 'displayName'
  | 'investorCode'
  | 'statusCode'
  | 'gerePar'
  | 'consultant'
  | 'personnePhysiqueIds'
  | 'relationCount'
  | 'gedDocumentCount'
  | 'viewerCommandCount'
  | 'boxPlacementOperations'
  | 'immoOptionIntention'
  | 'corrections'
  | 'anomalies'
  | 'metadatas'
  | 'deletionScore'
  | 'actions';

type InvestorComparisonRow = {
  key: InvestorComparisonRowKey;
  label: string;
  hasDifferences: boolean;
};

@Component({
  selector: 'dbw-ariane-duplicate-inspection-page',
  imports: [
    MatTableModule,
    OuiButtonModule,
    OuiDialogModule,
    RouterLink,
    OuiTableModule,
    OuiTagModule,
    ConsultantCardComponent,
    InvestorCardComponent,
    InvestorDeletionScoreDetailsComponent,
    InvestorDetailsComponent,
    InvestorStatusTagComponent,
    StateCardComponent,
    SummaryGridComponent,
  ],
  templateUrl: './ariane-duplicate-inspection.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./ariane-duplicate-inspection.component.scss'],
})
export class ArianeDuplicateInspectionPageComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);
  protected readonly appRoutePaths = appRoutePaths;

  readonly arianeId = signal(0);
  readonly loading = signal(true);
  readonly error = signal<string | undefined>(undefined);
  readonly inspection = signal<ArianeDuplicateInspection | undefined>(undefined);

  readonly highlightedDifferences = computed(() => (this.inspection()?.differences ?? []).filter(difference => difference.hasDifferences));
  readonly investorComparisonRows = computed<InvestorComparisonRow[]>(() => {
    const investors = this.inspection()?.investors ?? [];
    const rows: Omit<InvestorComparisonRow, 'hasDifferences'>[] = [
      { key: 'displayName', label: 'Nom affiché' },
      { key: 'statusCode', label: 'Statut' },
      { key: 'gerePar', label: 'Géré par' },
      { key: 'consultant', label: 'Consultant' },
      { key: 'personnePhysiqueIds', label: 'Personnes physiques Atlas' },
      { key: 'relationCount', label: 'Relations' },
      { key: 'viewerCommandCount', label: 'Commandes' },
      { key: 'gedDocumentCount', label: 'Documents GED' },
      { key: 'boxPlacementOperations', label: 'Opérations Box' },
      { key: 'immoOptionIntention', label: 'Intention option Immo' },
      { key: 'metadatas', label: 'Metadatas Minotaure' },
      { key: 'deletionScore', label: 'Score de suppression' },
      { key: 'actions', label: 'Actions' },
    ];

    if (this.hasMinotaureMetadatas(investors)) {
      rows.splice(
        rows.findIndex(row => row.key === 'actions'),
        0,
        { key: 'corrections', label: 'Corrections Minotaure' },
        { key: 'anomalies', label: 'Anomalies Minotaure' }
      );
    }

    return rows.map(row => ({
      ...row,
      hasDifferences: row.key === 'actions' ? false : this.hasComparisonDifference(row.key, investors),
    }));
  });
  readonly deletionCandidates = computed<ArianeDeletionCandidateView[]>(() => {
    const inspection = this.inspection();

    if (!inspection) {
      return [];
    }

    return inspection.deletionAnalysis.rankedCandidates.map(candidate => ({
      ...candidate,
      investor: inspection.investors.find(investor => investor.investor.investorId === candidate.investorId),
    }));
  });
  readonly canExecutePurgeAction = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.investorPurgeExecute)
  );

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly dialogService: OuiDialogService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(paramMap => {
      const arianeId = Number(paramMap.get('arianeId'));

      if (!Number.isFinite(arianeId) || arianeId <= 0) {
        this.error.set('ArianeId invalide.');
        this.loading.set(false);
        return;
      }

      this.arianeId.set(arianeId);
      this.loadInspection();
    });
  }

  reload(): void {
    this.loadInspection();
  }

  navigateBack(): void {
    this.router.navigateByUrl(appRoutePaths.atlas.doublonsAriane);
  }

  openInvestor(investorId: number): void {
    this.router.navigateByUrl(appRoutePaths.atlas.investor(investorId));
  }

  openMetadata(metadataId: number): void {
    this.router.navigateByUrl(appRoutePaths.minotaure.metadata(metadataId));
  }

  openPurgeDialog(investor: InvestorInspection): void {
    if (!this.canExecutePurgeAction()) {
      return;
    }

    this.dialogService.openDialog(
      InvestorPurgeCheckDialogComponent,
      {
        investorId: investor.investor.investorId,
        investorLabel: investor.investor.displayName || `Investisseur #${investor.investor.investorId}`,
      },
      undefined,
      '820px'
    );
  }

  getCorrectionMetadataCount(investor: InvestorInspection): number {
    return countDistinctMetadataIds(investor.corrections);
  }

  getAnomalyMetadataCount(investor: InvestorInspection): number {
    return countDistinctMetadataIds(investor.anomalies);
  }

  getCorrectionMetadataIds(investor: InvestorInspection): number[] {
    return this.getDistinctMetadataIds(investor.corrections.map(row => row.metadataId));
  }

  getAnomalyMetadataIds(investor: InvestorInspection): number[] {
    return this.getDistinctMetadataIds(investor.anomalies.map(row => row.metadataId));
  }

  formatPersonnePhysiqueIds(ids: number[]): string {
    return formatAtlasPersonnePhysiqueIds(ids);
  }

  formatGedDocumentCount(gedDocumentCount: number | null | undefined): string {
    if (gedDocumentCount === null || gedDocumentCount === undefined) {
      return 'Indisponible';
    }

    return `${gedDocumentCount}`;
  }

  formatViewerCommandCount(commandCount: number | null | undefined, contractCount: number | null | undefined): string {
    if (commandCount === null || commandCount === undefined) {
      return 'Indisponible';
    }

    if (contractCount === null || contractCount === undefined) {
      return `${commandCount} commande(s)`;
    }

    return `${commandCount} commande(s) - ${contractCount} contrat(s) renseigné(s)`;
  }

  getBooleanLabel(value: boolean | null | undefined): string {
    if (value === null || value === undefined) {
      return 'Indisponible';
    }

    return value ? 'Oui' : 'Non';
  }

  getBooleanStatus(value: boolean | null | undefined): 'completed' | 'todo' | 'pending' {
    if (value === null || value === undefined) {
      return 'pending';
    }

    return value ? 'todo' : 'completed';
  }

  getGereParLabel(gerePar: number | null | undefined): string {
    return formatAtlasGerePar(gerePar);
  }

  trackDeletionCandidate(_: number, candidate: ArianeDeletionCandidateView): number {
    return candidate.investorId;
  }

  trackComparisonRow(_: number, row: InvestorComparisonRow): string {
    return row.key;
  }

  getDeletionCandidate(investorId: number): ArianeDeletionCandidateView | undefined {
    return this.deletionCandidates().find(candidate => candidate.investorId === investorId);
  }

  getDeletionDecisionLabel(candidate: ArianeDeletionCandidateView | undefined): string {
    if (!candidate) {
      return 'Non calculé';
    }

    if (candidate.isAutomaticSuggestion) {
      return 'Suggestion auto';
    }

    return this.inspection()?.deletionAnalysis.requiresManualVerification ? 'À vérifier' : 'Non retenu';
  }

  getDeletionDecisionStatus(candidate: ArianeDeletionCandidateView | undefined): 'completed' | 'to-correct' | 'pending' {
    if (candidate?.isAutomaticSuggestion) {
      return 'completed';
    }

    return this.inspection()?.deletionAnalysis.requiresManualVerification ? 'to-correct' : 'pending';
  }

  getCrossConsultantRelationsStatus(investor: InvestorInspection): 'completed' | 'error' {
    return investor.hasCrossConsultantRelations ? 'error' : 'completed';
  }

  getCrossConsultantRelationsLabel(investor: InvestorInspection): string {
    if (!investor.hasCrossConsultantRelations) {
      return 'Aucune relation vers un autre consultant';
    }

    return `${investor.crossConsultantRelationCount} relation(s) vers ${investor.crossConsultantRelatedInvestorCount} investisseur(s) d'un autre consultant`;
  }

  getComparisonDisplayValue(key: InvestorComparisonRowKey, investor: InvestorInspection): string {
    switch (key) {
      case 'displayName':
        return investor.investor.displayName || '-';
      case 'investorCode':
        return investor.investor.investorCode || '-';
      case 'statusCode':
        return investor.investor.statusCode || '-';
      case 'gerePar':
        return this.getGereParLabel(investor.investor.gerePar);
      case 'consultant':
        return [investor.investor.consultantCode, investor.investor.consultantName].filter(value => !!value?.trim()).join(' - ') || '-';
      case 'personnePhysiqueIds':
        return this.formatPersonnePhysiqueIds(investor.investor.personnePhysiqueIds);
      case 'relationCount':
        return `${investor.relationCount} - ${this.getCrossConsultantRelationsLabel(investor)}`;
      case 'viewerCommandCount':
        return this.formatViewerCommandCount(investor.viewerCommandCount, investor.viewerContractCount);
      case 'gedDocumentCount':
        return this.formatGedDocumentCount(investor.gedDocumentCount);
      case 'boxPlacementOperations':
        return this.getBooleanLabel(investor.hasBoxPlacementOperations);
      case 'immoOptionIntention':
        return this.getBooleanLabel(investor.hasImmoOptionIntention);
      case 'corrections':
        return this.formatMetadataCountAndIds(
          'correction',
          investor.corrections.map(row => row.metadataId)
        );
      case 'anomalies':
        return this.formatMetadataCountAndIds(
          'anomalie',
          investor.anomalies.map(row => row.metadataId)
        );
      case 'metadatas':
        return investor.metadatas.length === 0
          ? 'Aucune'
          : investor.metadatas
              .map(metadata => {
                const suffix = metadata.investorIsAutomaticSuggestion ? ' (suggestion auto)' : '';
                return `${metadata.metadataId}: ${this.getMetadataBadgeLabel(metadata)}${suffix}`;
              })
              .join(', ');
      case 'deletionScore':
        return `${this.getDeletionCandidate(investor.investor.investorId)?.totalScore ?? '-'}`;
      case 'actions':
        return '';
    }
  }

  readonly getMetadataBadgeStatus = getMetadataBadgeStatus;
  readonly getMetadataBadgeLabel = getMetadataBadgeLabel;

  trackDifference(_: number, difference: ArianeDuplicateDifference): string {
    return difference.fieldKey;
  }

  private hasComparisonDifference(key: InvestorComparisonRowKey, investors: InvestorInspection[]): boolean {
    if (investors.length < 2) {
      return false;
    }

    return new Set(investors.map(investor => this.getComparisonValue(key, investor))).size > 1;
  }

  private hasMinotaureMetadatas(investors: InvestorInspection[]): boolean {
    return investors.some(investor => investor.metadatas.length > 0);
  }

  private getInvestorInspection(investorId: number): InvestorInspection | undefined {
    return this.inspection()?.investors.find(investor => investor.investor.investorId === investorId);
  }

  private formatMetadataCountAndIds(label: string, metadataIds: number[]): string {
    const distinctIds = this.getDistinctMetadataIds(metadataIds);

    if (distinctIds.length === 0) {
      return `0 ${label}`;
    }

    return `${distinctIds.length} ${label}${distinctIds.length > 1 ? 's' : ''}: ${distinctIds.join(', ')}`;
  }

  private getDistinctMetadataIds(metadataIds: number[]): number[] {
    return [...new Set(metadataIds)].sort((left, right) => left - right);
  }

  private getComparisonValue(key: InvestorComparisonRowKey, investor: InvestorInspection): string {
    switch (key) {
      case 'displayName':
        return this.normalizeComparisonValue(investor.investor.displayName);
      case 'investorCode':
        return this.normalizeComparisonValue(investor.investor.investorCode);
      case 'statusCode':
        return this.normalizeComparisonValue(investor.investor.statusCode);
      case 'gerePar':
        return this.normalizeComparisonValue(this.getGereParLabel(investor.investor.gerePar));
      case 'consultant':
        return this.normalizeComparisonValue(
          [
            investor.investor.consultantId,
            investor.investor.consultantCode,
            investor.investor.consultantName,
            investor.investor.consultantStatusCode,
          ].join('|')
        );
      case 'personnePhysiqueIds':
        return investor.investor.personnePhysiqueIds.join('|');
      case 'relationCount':
        return [
          investor.relationCount,
          investor.hasCrossConsultantRelations,
          investor.crossConsultantRelationCount,
          investor.crossConsultantRelatedInvestorCount,
        ].join('|');
      case 'viewerCommandCount':
        return [
          investor.viewerCommandCount === null || investor.viewerCommandCount === undefined ? '' : investor.viewerCommandCount,
          investor.viewerContractCount === null || investor.viewerContractCount === undefined ? '' : investor.viewerContractCount,
        ].join('|');
      case 'gedDocumentCount':
        return investor.gedDocumentCount === null || investor.gedDocumentCount === undefined ? '' : `${investor.gedDocumentCount}`;
      case 'boxPlacementOperations':
        return investor.hasBoxPlacementOperations === null || investor.hasBoxPlacementOperations === undefined
          ? ''
          : `${investor.hasBoxPlacementOperations}`;
      case 'immoOptionIntention':
        return investor.hasImmoOptionIntention === null || investor.hasImmoOptionIntention === undefined
          ? ''
          : `${investor.hasImmoOptionIntention}`;
      case 'corrections':
        return investor.corrections
          .map(row => row.metadataId)
          .sort((left, right) => left - right)
          .join('|');
      case 'anomalies':
        return investor.anomalies
          .map(row => row.metadataId)
          .sort((left, right) => left - right)
          .join('|');
      case 'metadatas':
        return investor.metadatas
          .map(metadata => `${metadata.metadataId}:${metadata.investorIsAutomaticSuggestion}:${this.getMetadataBadgeLabel(metadata)}`)
          .sort()
          .join('|');
      case 'deletionScore':
        return `${this.getDeletionCandidate(investor.investor.investorId)?.totalScore ?? ''}`;
      case 'actions':
        return '';
    }
  }

  private normalizeComparisonValue(value: unknown): string {
    return `${value ?? ''}`.trim().toUpperCase();
  }

  private loadInspection(): void {
    this.loading.set(true);
    this.error.set(undefined);

    this.apiService.getArianeDuplicateInspection(this.arianeId()).subscribe({
      next: inspection => {
        this.inspection.set(inspection);
        this.loading.set(false);
      },
      error: error => {
        this.inspection.set(undefined);
        this.error.set(
          error?.status === 404
            ? 'Le doublon Ariane demandé est introuvable.'
            : (error?.error?.detail ?? "Impossible de charger l'inspection du doublon Ariane.")
        );
        this.loading.set(false);
      },
    });
  }
}
